import { useCallback, useEffect, useRef, useState } from 'react';
import {
	InvestmentProgrammeDocumentDetailsRes,
	SignatureType,
} from '@dealstack/shared/lib/generated';
import { MAX_PENDING_ACTION_DURATION_MS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/constants';
import {
	hasPendingActionSettled,
	isSignerAssignmentAction,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingDocumentAction.utils';
import {
	PendingDocumentAction,
	PendingSignerAssignmentAction,
	UsePendingDocumentActionArgs,
	UsePendingDocumentActionResult,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/usePendingDocumentAction.types';

export const usePendingDocumentAction = ({
	documentDetails,
	refetch,
}: UsePendingDocumentActionArgs): UsePendingDocumentActionResult => {
	const [pendingAction, setPendingAction] = useState<PendingDocumentAction | null>(null);
	const [isActionTimedOut, setIsActionTimedOut] = useState(false);
	const timeoutRef = useRef<number | null>(null);
	// The document as it looked when the action began. Settling is only
	// meaningful against something newer than this.
	const snapshotRef = useRef<InvestmentProgrammeDocumentDetailsRes | undefined>(undefined);

	const clearTimeoutRef = useCallback(() => {
		if (timeoutRef.current !== null) {
			window.clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
	}, []);

	const clearPendingAction = useCallback(() => {
		clearTimeoutRef();
		setPendingAction(null);
		setIsActionTimedOut(false);
	}, [clearTimeoutRef]);

	const beginPendingAction = useCallback(
		(action: PendingDocumentAction) => {
			// Electronic assign/unassign completes inside the request — there is no
			// async backend work to wait on, so a single refetch is the whole job.
			if (
				isSignerAssignmentAction(action) &&
				documentDetails?.signatureType === SignatureType.ELECTRONIC
			) {
				refetch();
				return;
			}

			clearTimeoutRef();
			setIsActionTimedOut(false);
			snapshotRef.current = documentDetails;
			setPendingAction(action);
			timeoutRef.current = window.setTimeout(() => {
				// One last attempt before giving up: with polling gone this is the
				refetch();
				setIsActionTimedOut(true);
				setPendingAction(null);
				timeoutRef.current = null;
			}, MAX_PENDING_ACTION_DURATION_MS);

			refetch();
		},
		[documentDetails, refetch, clearTimeoutRef]
	);

	// Settle against whatever the stream last delivered. Runs on every change to
	// the document rather than on a timer, so the pending state clears as soon as
	// the backend's work shows up.
	useEffect(() => {
		if (!pendingAction) {
			return;
		}
		// This effect also runs the moment the action begins, when `documentDetails`
		// is still the pre-action snapshot. Some predicates are already satisfied
		// there — claiming a signer that is past CREATED, say — so settling on it
		// would clear the pending state before the backend has done anything.
		if (documentDetails === snapshotRef.current) {
			return;
		}
		if (hasPendingActionSettled({ action: pendingAction, documentDetails })) {
			clearPendingAction();
		}
	}, [documentDetails, pendingAction, clearPendingAction]);

	useEffect(() => clearTimeoutRef, [clearTimeoutRef]);

	const pendingSignerAssignmentAction: PendingSignerAssignmentAction | null =
		isSignerAssignmentAction(pendingAction) ? pendingAction : null;

	return {
		isActionPending: pendingAction !== null,
		pendingSignerAssignmentAction,
		isActionTimedOut,
		beginPendingAction,
		clearPendingAction,
	};
};
